import { Stack, Typography, useTheme } from "@mui/material";
import { TrendingUp } from "lucide-react";
import { useState } from "react";
import {
	type Control,
	type FieldValues,
	type Path,
	useFieldArray,
	useWatch,
} from "react-hook-form";
import { ClientPortfolioToolTypeEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { InvestmentToolIdentificationFields } from "web_myplann/Portfolio/Forms/InvestmentForm/InvestmentToolIdentificationFields";
import {
	getNewInvestmentToolDefaultValues,
	INVESTMENT_INSTRUCTION_DEFAULT_VALUES,
	INVESTMENT_TOOL_DEFAULT_VALUES,
} from "web_myplann/Portfolio/Forms/InvestmentForm/investmentToolDefaults";
import {
	getInvestmentInstructionFormSchema,
	getInvestmentToolSchema,
	type InvestmentToolInput,
} from "web_myplann/Portfolio/Forms/InvestmentForm/investmentToolSchema";
import {
	findDuplicateIsinIndex,
	mergeToolInstructions,
} from "web_myplann/Portfolio/Forms/InvestmentForm/mergeDuplicateIsinTool";
import { useInvestmentToolRowLabels } from "web_myplann/Portfolio/Forms/InvestmentForm/useInvestmentToolRowLabels";
import {
	ToolDrawer,
	type ToolVariant,
} from "web_myplann/Portfolio/Forms/ToolsShared/ToolDrawer";
import { AddRowButton } from "web_myplann/Shared/Components/AddRowButton";
import { ListRow } from "web_myplann/Shared/Components/ListRow";
import { MyPlannDialog } from "web_myplann/Shared/Components/MyPlannDialog";
import { normalizeIsin } from "web_myplann/Shared/Utils/isin";

type InvestmentToolsSectionProps<
	FormType extends FieldValues,
	TContext,
	TTransformed extends FieldValues | undefined,
> = {
	control: Control<FormType, TContext, TTransformed>;
	defaultToolType?: ClientPortfolioToolTypeEnum;
	mergeDuplicateIsin?: boolean;
};

type PendingMerge = {
	tool: InvestmentToolInput;
	duplicateIndex: number;
	editedIndex: number | null;
};

const PageResources = Resources.Portfolio.AddTools;

export const InvestmentToolsSection = <
	FormType extends FieldValues,
	TContext,
	TTransformed extends FieldValues | undefined,
>({
	control,
	defaultToolType,
	mergeDuplicateIsin = false,
}: InvestmentToolsSectionProps<FormType, TContext, TTransformed>) => {
	const { t } = useResource();
	const theme = useTheme();
	const [editedTool, setEditedTool] = useState<number | null>(null);
	const [isToolOpen, setIsToolOpen] = useState(false);
	const [pendingMerge, setPendingMerge] = useState<PendingMerge | null>(null);

	const toolsName = "tools" as Path<FormType>;
	const tools = useFieldArray({
		control,
		name: toolsName as never,
	});
	const toolValues: InvestmentToolInput[] =
		useWatch({ control, name: toolsName }) ?? [];
	const getRowLabels = useInvestmentToolRowLabels();

	const openTool = (index: number | null) => {
		setEditedTool(index);
		setIsToolOpen(true);
	};

	const getVariant = (tool: FieldValues): ToolVariant => ({
		typeLabels:
			tool.type === ClientPortfolioToolTypeEnum.BankProduct
				? "bankProduct"
				: "investment",
		showInstructionCount: tool.type === ClientPortfolioToolTypeEnum.Crypto,
		showValues: true,
		nameLabel:
			tool.type === ClientPortfolioToolTypeEnum.BankProduct
				? t(PageResources.Fields.accountName)
				: undefined,
		instructionsHint: tool.isAdvisorBond
			? t(PageResources.bondInstructionsHint)
			: undefined,
	});

	const saveTool = (tool: InvestmentToolInput) => {
		const duplicateIndex = mergeDuplicateIsin
			? findDuplicateIsinIndex(toolValues, tool, editedTool)
			: -1;
		if (duplicateIndex >= 0) {
			setPendingMerge({ tool, duplicateIndex, editedIndex: editedTool });
			return;
		}

		if (editedTool === null) {
			tools.append(tool as never);
		} else {
			tools.update(editedTool, tool as never);
		}
		setIsToolOpen(false);
	};

	const removeTool = () => {
		if (editedTool !== null) {
			tools.remove(editedTool);
		}
		setIsToolOpen(false);
	};

	const canRemoveTool =
		editedTool !== null && !toolValues[editedTool]?.isAdvisorBond;

	const resolveMerge = (confirmed: boolean) => {
		if (confirmed && pendingMerge) {
			const { tool, duplicateIndex, editedIndex } = pendingMerge;
			tools.update(
				duplicateIndex,
				mergeToolInstructions(toolValues[duplicateIndex], tool) as never,
			);
			if (editedIndex !== null) {
				tools.remove(editedIndex);
			}
			setIsToolOpen(false);
		}
		setPendingMerge(null);
	};

	const duplicateToolName = pendingMerge
		? toolValues[pendingMerge.duplicateIndex]?.name
		: null;
	const mergeDescriptionParams = {
		isin: normalizeIsin(pendingMerge?.tool.identification ?? ""),
		name: duplicateToolName,
	};

	return (
		<>
			<Typography variant="sectionLabel">
				{t(PageResources.Sections.investmentTools)}
			</Typography>

			<Stack bgcolor={theme.palette.background.paper}>
				{tools.fields.map((field, index) => (
					<ListRow
						key={field.id}
						{...getRowLabels(toolValues[index])}
						icon={<TrendingUp size={18} color={theme.palette.purple[600]} />}
						iconBg={theme.palette.purple[50]}
						rightTextStrong
						showChevron
						isLast={index === tools.fields.length - 1}
						onClick={() => openTool(index)}
					/>
				))}
				<AddRowButton
					label={t(PageResources.Actions.addTool)}
					onClick={() => openTool(null)}
				/>
			</Stack>

			<ToolDrawer
				open={isToolOpen}
				onClose={() => setIsToolOpen(false)}
				schema={getInvestmentToolSchema(t)}
				defaultValues={
					editedTool === null
						? getNewInvestmentToolDefaultValues(defaultToolType)
						: (toolValues[editedTool] ?? INVESTMENT_TOOL_DEFAULT_VALUES)
				}
				onSave={saveTool}
				onRemove={canRemoveTool ? removeTool : undefined}
				isNew={editedTool === null}
				instructions={{
					name: "instructions",
					schema: getInvestmentInstructionFormSchema(t),
					defaults: INVESTMENT_INSTRUCTION_DEFAULT_VALUES,
				}}
				getVariant={getVariant}
				renderIdentification={(form) => (
					<InvestmentToolIdentificationFields form={form} />
				)}
			/>

			<MyPlannDialog
				open={pendingMerge !== null}
				handleResult={resolveMerge}
				modalTitle={t(PageResources.DuplicateIsin.title)}
				modalContent={
					duplicateToolName
						? t(
								PageResources.DuplicateIsin.descriptionWithName,
								mergeDescriptionParams,
							)
						: t(PageResources.DuplicateIsin.description, mergeDescriptionParams)
				}
				confirmButtonText={t(PageResources.DuplicateIsin.confirm)}
				cancelButtonText={t(PageResources.DuplicateIsin.cancel)}
			/>
		</>
	);
};
